import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { ApiError, BackendErrorCode } from '@/modules/common/api/errors';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidatePaymentDetailsQueries } from '@/modules/common/utils/query-invalidation';

import { updatePaymentDetails } from '../../api/payment-details.api';

export const useUpdatePaymentDetails = ({
  onSuccess,
  onDuplicateName,
}: {
  onSuccess?: () => void;
  /** Called instead of the fallback toast when the backend rejects a duplicate name. */
  onDuplicateName?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.settings');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: updatePaymentDetails,
    onSuccess: async () => {
      await invalidatePaymentDetailsQueries(queryClient);
      displayToastSuccess(t('pages.finance.paymentDetails.toasts.updated'));
      onSuccess?.();
    },
    onError: error => {
      if (
        onDuplicateName &&
        error instanceof ApiError &&
        error.backendCode === BackendErrorCode.PAYMENT_DETAILS_NAME_DUPLICATE
      ) {
        onDuplicateName();
        return;
      }
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.settings:api.error.paymentDetails.updateFailed'
      );
    },
  });
};
